/**
 * Real-fix-to-real-fix linear interpolation, replacing heading/speed dead-reckoning
 * (the old coastPosition/coastBusPosition) across every live vehicle map (Ultratech,
 * WonderCement, MBMT, TUPL, Nagpur).
 *
 * The dead-reckoning approach guessed a position forward from the last real fix using
 * reported heading+speed, capped well short of the real inter-fix gap -- so a marker
 * glided briefly, froze for most of the gap, then snapped when the next real fix landed
 * and corrected the guess. This module never guesses a direction: it lerps from wherever
 * the vehicle is currently DISPLAYED to each new REAL fix, over the measured gap between
 * fixes. The glide's endpoint is always a real fix, so there is never a wrong guess to
 * snap-correct, and the glide spans the whole gap, so there is no freeze.
 *
 * Plain functions + a caller-owned Map (not a class), matching this layer's existing
 * style (coastPosition/spreadCoincident) -- each tick loop holds its own TweenStore in a
 * ref, since lerping needs state across frames that a pure function can't carry.
 */

export interface TweenState {
  /** Epoch (ms) of the real fix this tween is animating toward. */
  fixEpochMs: number
  /** Displayed position when this tween started. */
  fromLat: number
  fromLon: number
  /** The target real fix, unmodified. */
  toLat: number
  toLon: number
  /** Wall-clock (nowMs) when this tween started. */
  startedAtMs: number
  /** Clamped tween duration. */
  durationMs: number
}

export type TweenStore = Map<string, TweenState>

export interface TweenOptions {
  /** Floor on tween duration, guards against near-zero/negative measured gaps (clock
   *  jitter, quantized epoch estimates) producing a divide-by-~0 ease rate. */
  minDurationMs?: number
  /** Ceiling on tween duration. Comfortably covers the largest known normal site
   *  cadence (Nagpur/MBMT ~60s) plus slack for a late poll, so no real call site needs
   *  to override this. */
  maxDurationMs?: number
  /** A newly observed fixEpochMs within this tolerance of the stored one is treated as
   *  "same fix, no new data" rather than a new fix to tween toward -- required because
   *  MBMT/TUPL's derived epoch (fetchedAt - seen_s*1000) is not bit-stable poll to poll
   *  even when the underlying fix hasn't changed (serving/mbmt.py's seen_s rounds to
   *  100ms, and real request-latency variance adds more on top -- see
   *  DEFAULT_SAME_FIX_TOLERANCE_MS's own comment). Far below the real ~60s gap either
   *  way, so it can't mask a genuinely new fix. */
  sameFixToleranceMs?: number
  /** A measured gap (newEpoch - storedEpoch) beyond this snaps instead of gliding --
   *  e.g. a vehicle reappearing after being offline for hours should snap immediately,
   *  not glide across the intervening distance over maxDurationMs. */
  snapIfGapExceedsMs?: number
}

const DEFAULT_MIN_DURATION_MS = 1_000
const DEFAULT_MAX_DURATION_MS = 75_000
// Adversarial review (Phase 4, round 1) found the first-draft 2000ms default could
// misfire on MBMT/TUPL's derived epoch: a same-fix poll whose round-trip latency varies
// by more than 2s between two consecutive polls could misread as a genuinely new fix
// with an artificially short gap, shortening that glide and then freezing for the
// remainder of the real ~60s cadence (the same symptom this module exists to remove,
// reintroduced via a different path). 5000ms is a safety-margin judgement call, NOT a
// measured figure for /api/mbmt/live itself (round 2 of the same review caught an
// earlier draft of this comment mis-citing a 2-2.8s latency figure that actually came
// from a different, now-deleted endpoint's docstring, measuring a bulk route-stops
// query, not this poll) -- still comfortably below the real ~60s floor regardless.
const DEFAULT_SAME_FIX_TOLERANCE_MS = 5_000
const DEFAULT_SNAP_IF_GAP_EXCEEDS_MS = 180_000

/** Mirrors serving/app.py's own NAGPUR_TRAIL_MAX_FORWARD_SKEW_MS -- that function's own
 *  comment documents this exact device-clock/NTP failure mode already causing "a
 *  permanent data-loss bug" for this fleet once before. A device epoch more than this
 *  far ahead of wall-clock `now` is clock skew, not a real future fix. */
const MAX_FORWARD_SKEW_MS = 60_000

/**
 * Is this epoch usable as a tween target? Guards against two failure classes a caller
 * can otherwise feed in:
 *  - degenerate (a raw `0` from `last_geo_ts || updated_ts` when BOTH are falsy, or a
 *    non-finite value);
 *  - clock-skewed far into the future (a device NTP/clock bug -- confirmed previously
 *    live on this fleet, see MAX_FORWARD_SKEW_MS above).
 * Either one, once stored as a real anchor, is permanent: every later genuinely-older
 * real fix reads as "regressed epoch" (older than the bad one) and is discarded forever,
 * even as the raw fix keeps genuinely changing -- the exact freeze this module exists to
 * remove, reintroduced via a bad anchor instead of a bad guess. Every call site must
 * check this BEFORE calling `tweenedPosition` and fall back to the raw fix (matching the
 * deleted coastPosition's own `if (!origin) return raw fix` guard, which also implicitly
 * absorbed a future-skewed origin via its own `Math.max(0, ageS)` clamp) when it returns
 * false.
 */
export function isUsableFixEpoch(
  epochMs: number | null | undefined, nowMs: number,
): epochMs is number {
  return typeof epochMs === 'number' && Number.isFinite(epochMs) && epochMs > 0
    && epochMs <= nowMs + MAX_FORWARD_SKEW_MS
}

/**
 * Derives a real fix epoch for a backend with no absolute fix timestamp on the wire --
 * only `seenS` (seconds-ago as of the poll) plus the poll's own fetch wall-clock. Shared
 * by MBMT and TUPL (re-exported from their own map components as mbmtFixEpochMs/
 * tuplFixEpochMs so each file's locked test contract keeps its own name). Returns null
 * when `seenS` is unusable (missing/NaN on a sparse/cached record) -- the caller skips
 * the tween for that vehicle and falls back to its raw position, same as it already does
 * for a vehicle with no lat/lon fix at all.
 */
export function seenSFixEpochMs(fetchedAt: number, seenS: number | undefined | null): number | null {
  if (seenS == null || !Number.isFinite(seenS)) return null
  return fetchedAt - seenS * 1000
}

function easedPosition(state: TweenState, nowMs: number): { lat: number; lon: number } {
  const eased = state.durationMs <= 0
    ? 1
    : Math.min(1, Math.max(0, (nowMs - state.startedAtMs) / state.durationMs))
  return {
    lat: state.fromLat + (state.toLat - state.fromLat) * eased,
    lon: state.fromLon + (state.toLon - state.fromLon) * eased,
  }
}

/**
 * Advance/read the tween for one vehicle. Call once per vehicle per animation frame.
 * `store` is a long-lived Map the caller owns (one per tick loop, held in a ref).
 */
export function tweenedPosition(
  store: TweenStore,
  id: string,
  fix: { lat: number; lon: number },
  fixEpochMs: number,
  nowMs: number,
  opts?: TweenOptions,
): { lat: number; lon: number } {
  const minDurationMs = opts?.minDurationMs ?? DEFAULT_MIN_DURATION_MS
  const maxDurationMs = opts?.maxDurationMs ?? DEFAULT_MAX_DURATION_MS
  const sameFixToleranceMs = opts?.sameFixToleranceMs ?? DEFAULT_SAME_FIX_TOLERANCE_MS
  const snapIfGapExceedsMs = opts?.snapIfGapExceedsMs ?? DEFAULT_SNAP_IF_GAP_EXCEEDS_MS

  const existing = store.get(id)

  if (!existing) {
    // First sighting: snap, no glide from an undefined "from".
    const entry: TweenState = {
      fixEpochMs, fromLat: fix.lat, fromLon: fix.lon, toLat: fix.lat, toLon: fix.lon,
      startedAtMs: nowMs, durationMs: 0,
    }
    store.set(id, entry)
    return easedPosition(entry, nowMs)
  }

  const gap = fixEpochMs - existing.fixEpochMs
  if (gap > sameFixToleranceMs) {
    // Genuinely new fix. The new tween's "from" is wherever the OLD tween currently
    // reads, not the old tween's own target -- so a tween retargeted mid-glide starts
    // smoothly from its current on-screen position.
    const from = easedPosition(existing, nowMs)
    const snap = gap > snapIfGapExceedsMs
    const entry: TweenState = {
      fixEpochMs,
      fromLat: from.lat, fromLon: from.lon,
      toLat: fix.lat, toLon: fix.lon,
      startedAtMs: nowMs,
      durationMs: snap ? 0 : Math.min(maxDurationMs, Math.max(minDurationMs, gap)),
    }
    store.set(id, entry)
    return easedPosition(entry, nowMs)
  }

  // Same fix (within tolerance) or an older/regressed epoch (clock skew) -- in both
  // cases, ignore the incoming fix/coordinate entirely and keep easing the existing tween.
  return easedPosition(existing, nowMs)
}

/**
 * Remove entries for ids no longer present in the live roster, so a vehicle that drops
 * off and later reappears starts a fresh (snap) tween instead of resuming stale state.
 * Call once per poll (not per frame) with the current live id set.
 */
export function pruneTweens(store: TweenStore, liveIds: ReadonlySet<string>): void {
  for (const id of store.keys()) {
    if (!liveIds.has(id)) store.delete(id)
  }
}
